// eslint-disable-next-line max-classes-per-file
import _ from '@lodash';
import * as colors from '@mui/material/colors';
import { formatISO } from 'date-fns';

class EventEmitter {
	constructor() {
		this.events = {};
	}

	_getEventListByName(eventName) {
		if (typeof this.events[eventName] === 'undefined') {
			this.events[eventName] = new Set();
		}
		return this.events[eventName];
	}

	//subscribe
	on(eventName, fn) {
		this._getEventListByName(eventName).add(fn);
	}

	once(eventName, fn) {
		const self = this;

		const onceFn = (...args) => {
			self.removeListener(eventName, onceFn);
			fn.apply(self, args);
		};
		this.on(eventName, onceFn);
	}

	//dispatch
	emit(eventName, ...args) {
		this._getEventListByName(eventName).forEach(
			// eslint-disable-next-line func-names
			function (fn) {
				fn.apply(this, args);
			}.bind(this)
		);
	}

	//unsubscribe
	removeListener(eventName, fn) {
		this._getEventListByName(eventName).delete(fn);
	}
}

class ABSUtils {
	static filterArrayByString(mainArr, mainArrRestr, searchText) {
		if (searchText == null || searchText === '') {
			return mainArr;
		}

		searchText = searchText.toLowerCase();

		return mainArr.filter(itemObj => this.searchInObj(itemObj, mainArrRestr, searchText));
	}

	static searchInObj(itemObj, onlyThoseCols, searchText) {
		if (!itemObj) {
			return false;
		}
		const propArray = Object.keys(itemObj);
		for (let i = 0; i < propArray.length; i += 1) {
			const prop = propArray[i];
			const value = itemObj[prop];
			//check if restricted header column (only first level)
			if (onlyThoseCols == null || onlyThoseCols == [] || onlyThoseCols.includes(prop)) {
				if (typeof value === 'string') {
					if (this.searchInString(value, searchText)) {
						return true;
					}
				} else if (Array.isArray(value)) {
					if (this.searchInArray(value, searchText)) {
						return true;
					}
				}
				if (typeof value === 'object') {
					if (this.searchInObj(value, null, searchText)) {
						return true;
					}
				}
			}
		}
		return false;
	}

	static searchInArray(arr, searchText) {
		arr.forEach((value) => {
			if (typeof value === 'string') {
				if (this.searchInString(value, searchText)) {
					return true;
				}
			}

			if (typeof value === 'object') {
				if (this.searchInObj(value, null, searchText)) {
					return true;
				}
			}
			return false;
		});
		return false;
	}

	static searchInString(value, searchText) {
		return value.toLowerCase().includes(searchText);
	}

	static generateGUID() {
		function S4() {
			return Math.floor((1 + Math.random()) * 0x10000)
				.toString(16)
				.substring(1);
		}

		return S4() + S4();
	}

	static toggleInArray(item, array) {
		if (array.indexOf(item) === -1) {
			array.push(item);
		} else {
			array.splice(array.indexOf(item), 1);
		}
	}

	static handleize(text) {
		return text
			.toString()
			.toLowerCase()
			.replace(/\s+/g, '-') // Replace spaces with -
			.replace(/\W+/g, '') // Remove all non-word chars
			.replace(/--+/g, '-') // Replace multiple - with single -
			.replace(/^-+/, '') // Trim - from start of text
			.replace(/-+$/, ''); // Trim - from end of text
	}

	static setRoutes(config, defaultAuth) {
		let routes = [...config.routes];

		routes = routes.map((route) => {
			let auth = config.auth || config.auth === null ? config.auth : defaultAuth || null;
			auth = route.auth || route.auth === null ? route.auth : auth;
			const settings = _.merge({}, config.settings, route.settings);

			return {
				...route,
				settings,
				auth,
			};
		});

		return [...routes];
	}

	static generateRoutesFromConfigs(configs, defaultAuth) {
		let allRoutes = [];
		configs.forEach((config) => {
			allRoutes = [...allRoutes, ...this.setRoutes(config, defaultAuth)];
		});
		return allRoutes;
	}

	static findById(obj, id) {
		let i;
		let childObj;
		let result;

		if (id === obj.id) {
			return obj;
		}

		for (i = 0; i < Object.keys(obj).length; i += 1) {
			childObj = obj[Object.keys(obj)[i]];

			if (typeof childObj === 'object') {
				result = this.findById(childObj, id);
				if (result) {
					return result;
				}
			}
		}
		return false;
	}

	static getFlatNavigation(navigationItems, flatNavigation = []) {
		for (let i = 0; i < navigationItems.length; i += 1) {
			const navItem = navigationItems[i];

			if (navItem.type === 'item') {
				flatNavigation.push({
					id: navItem.id,
					title: navItem.title,
					type: navItem.type,
					icon: navItem.icon || false,
					url: navItem.url,
					auth: navItem.auth || null,
				});
			}

			if (navItem.type === 'collapse' || navItem.type === 'group') {
				if (navItem.children) {
					this.getFlatNavigation(navItem.children, flatNavigation);
				}
			}
		}
		return flatNavigation;
	}

	static randomMatColor(hue) {
		hue = hue || '400';
		const mainColors = [
			'red',
			'pink',
			'purple',
			'deepPurple',
			'indigo',
			'blue',
			'lightBlue',
			'cyan',
			'teal',
			'green',
			'lightGreen',
			'lime',
			'yellow',
			'amber',
			'orange',
			'deepOrange',
		];
		const randomColor = mainColors[Math.floor(Math.random() * mainColors.length)];
		return colors[randomColor][hue];
	}

	static difference(object, base) {
		function changes(_object, _base) {
			return _.transform(_object, (result, value, key) => {
				if (!_.isEqual(value, _base[key])) {
					result[key] =
						_.isObject(value) && _.isObject(_base[key]) ? changes(value, _base[key]) : value;
				}
			});
		}

		return changes(object, base);
	}

	static EventEmitter = EventEmitter;

	static updateNavItem(nav, id, item) {
		return nav.map((_item) => {
			if (_item.id === id) {
				return _.merge({}, _item, item);
			}

			if (_item.children) {
				return _.merge({}, _item, {
					children: this.updateNavItem(_item.children, id, item),
				});
			}

			return _.merge({}, _item);
		});
	}

	static removeNavItem(nav, id) {
		return nav
			.map((_item) => {
				if (_item.id === id) {
					return null;
				}

				if (_item.children) {
					return _.merge({}, _.omit(_item, ['children']), {
						children: this.removeNavItem(_item.children, id),
					});
				}

				return _.merge({}, _item);
			})
			.filter((s) => s);
	}

	static prependNavItem(nav, item, parentId) {
		if (!parentId) {
			return [item, ...nav];
		}

		return nav.map((_item) => {
			if (_item.id === parentId && _item.children) {
				return {
					..._item,
					children: [item, ..._item.children],
				};
			}

			if (_item.children) {
				return _.merge({}, _item, {
					children: this.prependNavItem(_item.children, item, parentId),
				});
			}

			return _.merge({}, _item);
		});
	}

	static appendNavItem(nav, item, parentId) {
		if (!parentId) {
			return [...nav, item];
		}

		return nav.map((_item) => {
			if (_item.id === parentId && _item.children) {
				return {
					..._item,
					children: [..._item.children, item],
				};
			}

			if (_item.children) {
				return _.merge({}, _item, {
					children: this.appendNavItem(_item.children, item, parentId),
				});
			}

			return _.merge({}, _item);
		});
	}

	static hasModulePermission(authArr, userModules) {
		/**
		 * If auth array is not defined
		 * Pass and allow (means free for all)
		 */
		if (authArr === null || authArr === undefined) {
			// console.info("auth is null || undefined:", authArr);
			return true;
		}
		if (authArr.length === 0) {
			/**
			 * if auth array is empty means,
			 * allow only user not logged in (null or empty[])
			 */
			// console.info("auth is empty[]:", authArr);
			return !userModules || userModules.length === 0;
		}
		/**
		 * Check if user has grants
		 */
		// console.info("auth arr:", authArr);
		/*
			Check if user modules is array,
		*/
		if (userModules && Array.isArray(userModules)) {
			return authArr.some(r => userModules.indexOf(r) >= 0);
		}
		/*
			Check if user modules is string,
		*/
		return authArr.includes(userModules);
	}

	static filterRecursive(data, predicate) {
		// if no data is sent in, return null, otherwise transform the data
		return !data
			? null
			: data.reduce((list, entry) => {
				let clone = null;
				if (predicate(entry)) {
					// if the object matches the filter, clone it as it is
					clone = { ...entry };
				}
				if (entry.children != null) {
					//first reset the clone
					clone = null;
					// if the object has childrens, filter the list of children
					const children = this.filterRecursive(entry.children, predicate);
					if (children.length > 0) {
						// if any of the children matches, clone the parent object, overwrite
						// the children list with the filtered list
						clone = { ...entry, children };
					}
				}

				// if there's a cloned object, push it to the output list
				if (clone) {
					list.push(clone);
				}
				return list;
			}, []);
	}

	static secondsToHms(seconds, returntype = "hm", addpluswhenpositive = false) {
		var h = (seconds / 3600) | 0;
		var m = (seconds % 3600 / 60) | 0;
		var s = (seconds % 3600 % 60) | 0;

		var hDisplay = h + "h";
		var mDisplay = Math.abs(m) + "m";
		var sDisplay = Math.abs(s) + "s";

		var returndata;
		switch (returntype) {
			case "h":
				returndata = hDisplay;
				break;
			case "m":
				returndata = mDisplay;
				break;
			case "s":
				returndata = sDisplay;
				break;
			case "hms":
				returndata = hDisplay + " " + mDisplay + " " + sDisplay;
				break;
			case "hh:mm:ss":
				returndata = ('0' + h).slice(-2) + ":" + ('0' + Math.abs(m)).slice(-2) + ":" + ('0' + Math.abs(s)).slice(-2);
				break;
			case "hh:mm":
				returndata = ('0' + h).slice(-2) + ":" + ('0' + Math.abs(m)).slice(-2);
				break;
			case "hm":
			default:
				returndata = hDisplay + " " + mDisplay;
				break;
		}

		if (addpluswhenpositive && !returndata.startsWith("-")) {
			if (seconds > 0) {
				return "+" + returndata;
			}
			else if (seconds < 0) {
				return "-" + returndata;
			}
			else
				return " " + returndata; //leading space wont work...
		}
		else
			return returndata;
	}

	static getAvatarFallbackCharArray(displayname) {
		if (displayname) {
			//only temporary until real displayname is implemented
			const displaynamearr = displayname.split(' ');
			var avatarchars = "";
			//take first chars of first and last item in displayname
			displaynamearr.forEach((element, index) => {
				if ((index == 0 || index == displaynamearr.length - 1) && element) {
					avatarchars += element[0];
				}
			});
		}
		return avatarchars;
	}

	//because of safari javascript core bug in date-fns format
	static NewDate(str) {
		if (str != null) {
			if (str instanceof Date) {
				return new Date(str);
			}
			else {
				return new Date(str.replace(/-/g, "/"));
			}
		}
		else {
			return new Date();
		}
	}

	//from https://stackoverflow.com/questions/6117814/get-week-of-year-in-javascript-like-in-php
	static getWeekNumber(d) {
		// Copy date so don't modify original
		d = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
		// Set to nearest Thursday: current date + 4 - current day number
		// Make Sunday's day number 7
		d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
		// Get first day of year
		var yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
		// Calculate full weeks to nearest Thursday
		var weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
		// Return array of year and week number
		return [d.getUTCFullYear(), weekNo];
	}

	static dateToDBStringFormat(d) {
		const pad = (num) => String(num).padStart(2, '0');
		var datestring = d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate()) + " " + pad(d.getHours()) + ":" + pad(d.getMinutes()) + ":" + pad(d.getSeconds());
		return datestring;
	}

	static findNearestNormTime(date, type, includemoredays, eperiod) {
		if (eperiod != null) {
			var hour_day_arr = ABSUtils.getHourArrofaDay(date, eperiod);

			if (hour_day_arr == null || hour_day_arr.norm == null || hour_day_arr.norm.length == 0 || hour_day_arr.norm.WorkingTimes == null || hour_day_arr.norm.WorkingTimes.length == 0) {
				//double check if we should go to the next day
				if (includemoredays) {
					var comingdate = ABSUtils.NewDate(date);
					if (type == "nextstart" || type == "nextend") {
						comingdate.setDate(comingdate.getDate() + 1);
						comingdate.setHours(0, 0, 0, 0);
					}
					else {
						comingdate.setDate(comingdate.getDate() - 1);
						comingdate.setHours(23, 59, 59, 999);
					}

					var nextdatenearest = ABSUtils.findNearestNormTime(comingdate, type, false, eperiod);

					return nextdatenearest;
				}
				else {
					return null;
				}
			}
			var arr = hour_day_arr.norm.WorkingTimes;

			switch (type) {
				case "nextstart":
					var nearest = ABSUtils.NewDate(date.toDateString() + " " + arr[0].From);
					var diff = Math.round(nearest - date);
					for (var val = 0; val < arr.length; val++) {
						var newdiff = Math.round(ABSUtils.NewDate(date.toDateString() + " " + arr[val].From) - date);
						if ((newdiff < diff && newdiff > 0) || ((newdiff < 0 || diff < 0) && newdiff > diff)) {
							diff = newdiff;
							nearest = ABSUtils.NewDate(date.toDateString() + " " + arr[val].From);
						}
					}

					//if nearest is still already gone, also try the next day
					if (nearest < date && includemoredays) {
						var nextdate = ABSUtils.NewDate(date);
						nextdate.setDate(nextdate.getDate() + 1);
						nextdate.setHours(0, 0, 0, 0);

						var nextdatenearest = ABSUtils.findNearestNormTime(nextdate, type, false, eperiod);

						if (nextdatenearest != null) {
							nearest = nextdatenearest;
						}
					}
					return nearest;
					break;
				case "nextend":
					var nearest = ABSUtils.NewDate(date.toDateString() + " " + arr[0].To);
					var diff = Math.round(nearest - date);
					for (var val = 0; val < arr.length; val++) {
						var newdiff = Math.round(ABSUtils.NewDate(date.toDateString() + " " + arr[val].To) - date);
						if ((newdiff < diff && newdiff > 0) || ((newdiff < 0 || diff < 0) && newdiff > diff)) {
							diff = newdiff;
							nearest = ABSUtils.NewDate(date.toDateString() + " " + arr[val].To);
						}
					}

					//if nearest is still already gone, also try the next day
					if (nearest < date && includemoredays) {
						var nextdate = ABSUtils.NewDate(date);
						nextdate.setDate(nextdate.getDate() + 1);
						nextdate.setHours(0, 0, 0, 0);

						var nextdatenearest = ABSUtils.findNearestNormTime(nextdate, type, false, eperiod);

						if (nextdatenearest != null) {
							nearest = nextdatenearest;
						}
					}
					return nearest;
					break;
				case "previousstart":
					var nearest = ABSUtils.NewDate(date.toDateString() + " " + arr[0].From);
					var diff = Math.round(date - nearest);
					for (var val = 0; val < arr.length; val++) {
						var newdiff = Math.round(date - ABSUtils.NewDate(date.toDateString() + " " + arr[val].From));
						if ((newdiff < diff && newdiff > 0) || ((newdiff < 0 || diff < 0) && newdiff > diff)) {
							diff = newdiff;
							nearest = ABSUtils.NewDate(date.toDateString() + " " + arr[val].From);
						}
					}

					//if nearest is still ahead of us, also try the previous day
					if (nearest > date && includemoredays) {
						var prevdate = ABSUtils.NewDate(date);
						prevdate.setDate(prevdate.getDate() - 1);
						prevdate.setHours(23, 59, 59, 999);

						var prevdatenearest = ABSUtils.findNearestNormTime(prevdate, type, false, eperiod);

						if (prevdatenearest != null) {
							nearest = prevdatenearest;
						}
					}
					return nearest;
					break;
				case "previousend":
					var nearest = ABSUtils.NewDate(date.toDateString() + " " + arr[0].To);
					var diff = Math.round(date - nearest);
					for (var val = 0; val < arr.length; val++) {
						var newdiff = Math.round(date - ABSUtils.NewDate(date.toDateString() + " " + arr[val].To));
						if ((newdiff < diff && newdiff > 0) || ((newdiff < 0 || diff < 0) && newdiff > diff)) {
							diff = newdiff;
							nearest = ABSUtils.NewDate(date.toDateString() + " " + arr[val].To);
						}
					}

					//if nearest is still ahead of us, also try the previous day
					if (nearest > date && includemoredays) {
						var prevdate = ABSUtils.NewDate(date);
						prevdate.setDate(prevdate.getDate() - 1);
						prevdate.setHours(23, 59, 59, 999);

						var prevdatenearest = ABSUtils.findNearestNormTime(prevdate, type, false, eperiod);

						if (prevdatenearest != null) {
							nearest = prevdatenearest;
						}
					}
					return nearest;
					break;
				default:
					return null;
			}
		}
		else {
			return null;
		}
	}
	static getHourArrofaDay(date, eperiod) {
		if (eperiod != null) {
			var dayofweek = date.toLocaleString('en-us', { weekday: 'long' });
			var calcmethod = eperiod.Normhours_Calculationmethod;
			var hour_day_arr = { norm: [], core: [], flex: [] };

			switch (calcmethod) {
				case "normhours": //calculate with fixed norm hours from employed period
				case "dailyhours": //fixed daily hours - so all settings should be same on each day
				case "none": //fixed daily hours - so all settings should be same on each day
					if (eperiod.Flexible_hours != null) {
						var flex_hour_arr = JSON.parse(eperiod.Flexible_hours);
						Object.keys(flex_hour_arr).forEach(key => {
							if (flex_hour_arr[key].DayName == dayofweek) {
								hour_day_arr.flex = flex_hour_arr[key];
							}
						});
					}

					var norm_hour_arr = JSON.parse(eperiod.Norm_hours);
					Object.keys(norm_hour_arr).forEach(key => {
						if (norm_hour_arr[key].DayName == dayofweek) {
							hour_day_arr.norm = norm_hour_arr[key];
						}
					});

					if (eperiod.Core_hours != null) {
						var core_hour_arr = JSON.parse(eperiod.Core_hours);
						Object.keys(core_hour_arr).forEach(key => {
							if (core_hour_arr[key].DayName == dayofweek) {
								hour_day_arr.core = core_hour_arr[key];
							}
						});
					}
					break;
				case "shifthours": //norm hours from shift plan - need to implement here
					hour_day_arr = null;
					break;
				default:
					hour_day_arr = null;
					break;
			}

			return hour_day_arr;
		}
		else {
			return null;
		}
	}

	static timelitestatusgroup = {
		"active_presence": "active_presence",
		"inactive_presence": "inactive_presence",
		"passive_presence": "passive_presence",
		"absence": "absence",
		"exemption": "exemption",
		"vacation": "vacation"
	};
	Objec
	static timelitestatusunit = {
		"auto": "auto",
		"second": "second",
		"minute": "minute",
		"hour": "hour",
		"halfday": "halfday",
		"day": "day"
	};
	static activeactivitytype = {
		"none": "none",
		"inside": "inside",
		"lead": "lead",
		"followup": "followup"
	};
	static timeliteactivitytype = {
		"live": "live",
		"create": "create",
		"edit": "edit",
		"delete": "delete",
	};
	static timeliterequesttype = {
		"activity": "activity",
		"seal_range": "seal_range",
		"seal_single": "seal_single",
	};

	static setDateFromNum(year, month, date, hours, minutes, seconds) {
		return formatISO(new Date(year, month, date, hours || '', minutes || '', seconds || ''));
	}

	static IsDarkMode() {
		var abs_darkmode = localStorage.getItem("abs_darkmode");
		if (abs_darkmode == 'true') {
			return true;
		}
		else
			return false;
	}

	//converts the image into base64 string
	static getBase64(file) {
		return new Promise((resolve, reject) => {
			let reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = function () {
				resolve(reader.result)
			};
			reader.onerror = function (error) {
				reject(error);
			};
		});
	}

	static roundNumber(number = 0, places = 0, addpluswhenpositive = false) {
		var newnumber = 0;

		if (number != null && number != undefined && !isNaN(number))
			newnumber = (number % 1 !== 0) ? number.toFixed(places) : number;
		else
			return 0;

		if (addpluswhenpositive && !newnumber.toString().startsWith("-")) {
			if (newnumber > 0) {
				return "+" + newnumber;
			}
			else if (newnumber < 0) {
				return "-" + newnumber;
			}
			else
				return " " + newnumber; //leading space wont work...
		}
		else
			return newnumber;
	}

	static invertColor(hex, bw) {
		try {
			if (hex.indexOf('#') === 0) {
				hex = hex.slice(1);
			}
			// convert 3-digit hex to 6-digits.
			if (hex.length === 3) {
				hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
			}
			if (hex.length !== 6) {
				throw new Error('Invalid HEX color.');
			}
			var r = parseInt(hex.slice(0, 2), 16),
				g = parseInt(hex.slice(2, 4), 16),
				b = parseInt(hex.slice(4, 6), 16);
			if (bw) {
				// https://stackoverflow.com/a/3943023/112731
				return (r * 0.299 + g * 0.587 + b * 0.114) > 186
					? '#000000'
					: '#FFFFFF';
			}
			// invert color components
			r = (255 - r).toString(16);
			g = (255 - g).toString(16);
			b = (255 - b).toString(16);
			// pad each with zeros and return
			return "#" + this.colorPadZero(r) + this.colorPadZero(g) + this.colorPadZero(b);
		}
		catch {
			return hex;
		}
	}

	static colorPadZero(str, len) {
		len = len || 2;
		var zeros = new Array(len).join('0');
		return (zeros + str).slice(-len);
	}

	static convertUmlautsForUsername(str) {
		if (!str) return str;

		// Convert German umlauts and special characters to ASCII equivalents
		const umlautMap = {
			'ä': 'ae',
			'ö': 'oe',
			'ü': 'ue',
			'Ä': 'ae',
			'Ö': 'oe',
			'Ü': 'ue',
			'ß': 'ss',
			'é': 'e',
			'è': 'e',
			'ê': 'e',
			'ë': 'e',
			'á': 'a',
			'à': 'a',
			'â': 'a',
			'ã': 'a',
			'å': 'a',
			'í': 'i',
			'ì': 'i',
			'î': 'i',
			'ï': 'i',
			'ó': 'o',
			'ò': 'o',
			'ô': 'o',
			'õ': 'o',
			'ú': 'u',
			'ù': 'u',
			'û': 'u',
			'ñ': 'n',
			'ç': 'c',
			'É': 'e',
			'È': 'e',
			'Ê': 'e',
			'Ë': 'e',
			'Á': 'a',
			'À': 'a',
			'Â': 'a',
			'Ã': 'a',
			'Å': 'a',
			'Í': 'i',
			'Ì': 'i',
			'Î': 'i',
			'Ï': 'i',
			'Ó': 'o',
			'Ò': 'o',
			'Ô': 'o',
			'Õ': 'o',
			'Ú': 'u',
			'Ù': 'u',
			'Û': 'u',
			'Ñ': 'n',
			'Ç': 'c'
		};

		return str.replace(/[äöüÄÖÜßéèêëáàâãåíìîïóòôõúùûñçÉÈÊËÁÀÂÃÅÍÌÎÏÓÒÔÕÚÙÛÑÇ]/g, (match) => umlautMap[match] || match);
	}
}

export default ABSUtils;

